import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { VideoExperience, newVideoFromMTObject, ImageExperienceVideo } from '@bingads-webui-campaign-react/image-experience-video-part';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { entityIs, propertyIs, andComp } from '@bingads-webui-campaign-react/form-error';
import { Stack } from '@bingads-webui-react/fabric-control';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withPreference } from '@bingads-webui-react/preference-context';
import { CopilotVideoEditor } from '@bingads-webui-campaign-react/edit-video-ads-view';
import { withAssetLibraryServicesLoaded } from '@bingads-webui-campaign-react/asset-library-services';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { TaskManager } from '@bingads-webui-universal/task-manager';
import { VideoTemplateEditorContextWrapper } from '@bingads-webui-campaign-react/video-ads-editor';

import { AddVideoButton } from './add-video-button';
import { IconButton } from '../common/shared-components';
import { VideoAdsSuggestionCarousel } from './video-ads-suggestion-carousel';
import { useVideoAdsCopilot } from './use-video-ads-copilot';
import { PlacementInfoWithStars } from './video-metadata';
import { ERROR_ENTITY } from '../common';

const entityIsAd = entityIs(ERROR_ENTITY.Ad);
// TODO - move to a common place.
const legacyTemplates = ['00000001-0000-0000-0000-000000000000', '00000001-0000-0000-0000-000000000001'];

export const validateVideo = (video, i18n, videoRequirementForDeal, skipQualityValidation) => {
  const messages = [];

  if (!video) {
    messages.push(i18n.getString(_TL_('Please add a video.')));
    return messages;
  }

  if (video.Status !== 'Active') {
    return [];
  }

  /* istanbul ignore next */
  if (video.quality === 0 && !skipQualityValidation) {
    messages.push(i18n.getString(_TL_('Your video is low quality and doesn\'t meet our requirements to serve on eligible networks. Please upload another file and try again.')));
  }

  if ((video.height < 720 || video.width < 960) && !skipQualityValidation) {
    messages.push(i18n.getString(_TL_('Your video must be at least 960x720 pixels. Please upload another file and try again.')));
  }

  /* istanbul ignore next */
  if (videoRequirementForDeal && video.quality < videoRequirementForDeal) {
    if (videoRequirementForDeal === 2) {
      messages.push(i18n.getString(_TL_('For this deal your asset must meet the General CTV requirements.')));
    } else if (videoRequirementForDeal === 4) {
      messages.push(i18n.getString(_TL_('For this deal your asset must meet the Premium Video requirements.')));
    }
  }

  if (messages.length > 1) {
    return video.Status === 'Active' ? [_.first(messages)] : [];
  }

  // for draft video, we only show processing error message.
  return messages;
};

const getStyles = (/* components, theme */) => ({
  videoPanelButton: {
    justifyContent: 'flex-end',
    display: 'none',
    cursor: 'pointer',
    borderRadius: 99,
    background: '#FFFFFF',
    width: 32,
    height: 32,
  },
  videoPanelButtonContainer: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    columnGap: '10px',
    display: 'flex',
  },
});

const CustomComponent = (props) => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onDataChange,
    idx,
    i18n,
    videoAd,
    updateValidation,
    videoRequirementForDeal,
    permissions,
    renderError,
    assetLibraryConfig,
    dataService,
    copilotEnabled,
    isCTV,
    multiVideoMode,
    isVideoRequired,
    skipQualityValidation,
    loadClipchampPlayer,
    useBrandKit,
    setUseBrandKit,
    isBrandKitEmpty,
    ...rest
  } = props;
  const {
    getVideo,
  } = dataService;
  const {
    cid, aid, perfMarker, serverContext,
  } = rest;

  const video = videoAd && videoAd.video ? videoAd.video : null;

  const videoTemplateUIEnabled = permissions && permissions.dynamic && permissions.dynamic.VideoTemplateUI;
  const isGoogleImportVideoAdNeedsRepair = permissions && permissions.IsGoogleImportVideoAdsEnabled
    && _.get(videoAd, 'video.sourceType') === 'GoogleImported'
    && _.get(videoAd, 'video.status') === 'Failed';

  const activity = useContext(ActivityContext);

  const classes = useDefaultStyles(getStyles);

  const [isUploading, setIsUploading] = useState(false);
  const [showVideoEditor, setShowVideoEditor] = useState(false);
  const [isFetchingVideo, setIsFetchingVideo] = useState(false);
  const [multiVideoList, setMultiVideoList] = useState(() => (videoAd && videoAd.videos ? [...videoAd.videos] : []));
  const [showMultiVideoCopilotEditor, setShowMultiVideoCopilotEditor] = useState({
    isOpen: false,
  });
  const [needsMetadataRefresh, setNeedsMetadataRefresh] = useState(false);

  const enableEdit = video && video.template;
  const templateId = video && video.template && video.template.templateId;
  const isLegacyTemplate = legacyTemplates.includes(templateId);

  const onErrorsUpdated = (errors) => {
    // already converted using i18n;

    if (errors) {
      updateValidation(errors, { video: null });
    }
  };

  const onUpdateVideoList = (list) => {
    if (list.length > 0) {
      const validationMessage = validateVideo(list[0], i18n, videoRequirementForDeal, skipQualityValidation);
      updateValidation(validationMessage, { video: list[0] });
      if (activity) {
        activity.signal(`onVideoValidated - messages: ${validationMessage} - isValid: ${validationMessage.length === 0}`);
      }
    } else {
      updateValidation([i18n.getString(_TL_('Please add a video.'))], { video: null });
    }
  };

  const onUpdateMultiAdVideoList = useCallback((list) => {
    const validVideos = list.filter(multiVideo => multiVideo !== undefined);
    setMultiVideoList(validVideos);

    let validationMessages = [];
    if (validVideos.length > 0) {
      validationMessages = validVideos.map(valVideo => validateVideo(valVideo, i18n, videoRequirementForDeal, skipQualityValidation));
    }

    const aggregatedMessages = validationMessages.flat();

    updateValidation(aggregatedMessages, { videos: validVideos });
    if (activity) {
      activity.signal(`onVideoValidated - messages: ${aggregatedMessages} - isValid: ${aggregatedMessages.length === 0}`);
    }
  }, [i18n, updateValidation, videoRequirementForDeal, activity, skipQualityValidation]);

  useEffect(() => {
    if (isUploading) {
      updateValidation([i18n.getString(_TL_('Please wait while we upload your video.'))], { video: null });

      if (activity) {
        activity.signal('video is uploading');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading]);

  useEffect(() => {
    const shouldDirty = !(_.isUndefined(videoAd) || _.isNull(videoAd));
    ((multiVideoMode === false) && updateValidation(
      videoAd ? validateVideo(videoAd.video, i18n, videoRequirementForDeal, skipQualityValidation) : [i18n.getString(_TL_('Please add a video.'))],
      { video: videoAd && videoAd.video ? videoAd.video : null },
      shouldDirty
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRenderItemDropdown = (values, videoAsset) => {
    if (values.uploadVideo !== null) {
      return null;
    }

    return (
      <div className={classes.videoPanelButtonContainer}>
        <div className={classNames([classes.videoPanelButton, 'edit-button'])}>
          <IconButton
            onClick={() => values.onReplace(videoAsset.Type)}
            iconProps={{ iconName: 'Edit' }}
            className="edit-icon-button"
            styles={{ root: { borderRadius: '99px', height: 32, width: 32 } }}
          />
        </div>
      </div>
    );
  };

  const onRenderItemDropdownCoPilot = (values) => {
    if (values.uploadVideo !== null) {
      return null;
    }

    return (
      <div className={classNames(classes.videoPanelButtonContainer)}>
        {enableEdit &&
          <div className={classNames(classes.videoPanelButton, 'edit-button')}>
            <IconButton
              onClick={() => setShowVideoEditor(true)}
              iconProps={{ iconName: 'Edit' }}
              className="edit-icon-button"
              styles={{ root: { borderRadius: '99px', height: 32, width: 32 } }}
            />
          </div>
        }
        <div className={classNames(classes.videoPanelButton, 'edit-button')}>
          <IconButton
            onClick={() => {
              const validationMessage = validateVideo(null, i18n, videoRequirementForDeal, skipQualityValidation);
              updateValidation(validationMessage, { video: null });
            }}
            iconProps={{ iconName: 'Cancel' }}
            className="remove-icon-button"
            styles={{ root: { borderRadius: '99px', height: 32, width: 32 } }}
          />
        </div>
      </div>
    );
  };

  const onRenderItemDropDownForMultiModeWithCopilot = useCallback((selectedVideo, selectedIdx) => {
    const selectedMultiVideo = multiVideoList[selectedIdx];


    return (
      <div style={{ display: 'flex', columnGap: 5 }}>
        {
          selectedVideo.template && (
            <div className={classNames(classes.videoPanelButton, 'hover-button')}>
              <IconButton
                onClick={() => setShowMultiVideoCopilotEditor({
                  isOpen: true,
                  video: selectedMultiVideo,
                  idx: selectedIdx,
                })}
                iconProps={{ iconName: 'Edit' }}
                className="edit-icon-button"
                styles={{ root: { borderRadius: '99px', height: 32, width: 32 } }}
              />
            </div>
          )
        }
        <div className={classNames(classes.videoPanelButton, 'hover-button')}>
          <IconButton
            onClick={() => {
              // remove only the selected video
              const updatedVideos = [...multiVideoList];
              updatedVideos.splice(selectedIdx, 1);
              onUpdateMultiAdVideoList(updatedVideos);
            }}

            iconProps={{ iconName: 'Cancel' }}
            className="remove-icon-button"
            styles={{ root: { borderRadius: '99px', height: 32, width: 32 } }}
          />
        </div>
      </div>
    );
  }, [multiVideoList, classes.videoPanelButton, onUpdateMultiAdVideoList]);

  const getNewVideoObject = useCallback((newVideo) => {
    const validationMessage = validateVideo(newVideo, i18n, videoRequirementForDeal, skipQualityValidation);
    const videoObject = new ImageExperienceVideo({
      ...newVideo,
      width: newVideo.template.width,
      height: newVideo.template.height,
      src: newVideo.src || newVideo.url,
      type: 'VideoMedia16X9',
      thumbnailUrl: newVideo.thumbnailUrl,
    });
    videoObject.url = newVideo.url;
    if (!videoObject.recommendationId) {
      videoObject.recommendationId = newVideo.recommendationId || newVideo.suggestionId;
    }

    return { videoObject, validationMessage };
  }, [i18n, videoRequirementForDeal, skipQualityValidation]);

  const updateWithNewVideo = useCallback((newVideo) => {
    const { videoObject, validationMessage } = getNewVideoObject(newVideo);

    if (multiVideoMode) {
      const isDuplicate = multiVideoList.find(v => v.recommendationId === videoObject.recommendationId);
      if (!isDuplicate) {
        onUpdateMultiAdVideoList([...multiVideoList, videoObject]);
      }
    } else {
      updateValidation(validationMessage, {
        video: videoObject,
      });
    }
  }, [getNewVideoObject, multiVideoMode, multiVideoList, onUpdateMultiAdVideoList, updateValidation]);

  const updateWithNewVideoAtIdx = useCallback((newVideo, newVideoIdx) => {
    const videoObject = new ImageExperienceVideo({
      ...newVideo,
      width: newVideo.template.width,
      height: newVideo.template.height,
      src: newVideo.src || newVideo.url,
      type: 'VideoMedia16X9',
      thumbnailUrl: newVideo.thumbnailUrl,
    });
    videoObject.url = newVideo.url;
    if (!videoObject.recommendationId) {
      videoObject.recommendationId = newVideo.recommendationId || newVideo.suggestionId;
    }

    const updatedVideos = [...multiVideoList];
    updatedVideos[newVideoIdx] = videoObject;
    onUpdateMultiAdVideoList(updatedVideos);
  }, [multiVideoList, onUpdateMultiAdVideoList]);

  const onChangeVideo = useCallback((newVideo) => {
    updateWithNewVideo({ ...newVideo, status: 'Active' });
  }, [updateWithNewVideo]);

  const onChangeMultiVideo = useCallback((newVideo, newVideoIdx) => {
    updateWithNewVideoAtIdx({ ...newVideo, status: 'Active' }, newVideoIdx);
  }, [updateWithNewVideoAtIdx]);

  useEffect(() => {
    if (!multiVideoMode && video && video.id && video.metadata && !video.template) {
      setIsFetchingVideo(true);
      getVideo({ id: video.id, fetchMetadata: true }).then((newVideo) => {
        updateWithNewVideo(newVideoFromMTObject(newVideo));
      }).finally(() => {
        setIsFetchingVideo(false);
      });
    }
  }, [getVideo, updateWithNewVideo, video, multiVideoMode]);

  // Track if any videos need metadata fetching
  useEffect(() => {
    if (multiVideoMode && multiVideoList.length > 0) {
      const hasVideosNeedingMetadata = multiVideoList.some(v => v && v.id && v.metadata && !v.template);
      setNeedsMetadataRefresh(hasVideosNeedingMetadata);
    }
  }, [multiVideoList, multiVideoMode]);

  useEffect(() => {
    if (!isFetchingVideo && needsMetadataRefresh && copilotEnabled && multiVideoMode && multiVideoList.length > 0) {
      setIsFetchingVideo(true);
      const getVideoPromises = [];
      const updatedVideos = [];
      multiVideoList.forEach((v, index) => {
        if (v && v.id && v.metadata && !v.template) {
          getVideoPromises.push(getVideo({ id: v.id, fetchMetadata: true }).then((newVideo) => {
            const { videoObject } = getNewVideoObject(newVideoFromMTObject(newVideo));
            updatedVideos.push([videoObject, index]);
          }));
        } else {
          updatedVideos.push([v, index]);
        }
      });

      Promise.all(getVideoPromises).finally(() => {
        const existingVideos = [...multiVideoList];
        updatedVideos.forEach(([newVideo, index]) => {
          existingVideos[index] = newVideo;
        });

        onUpdateMultiAdVideoList(existingVideos);
        setIsFetchingVideo(false);
        setNeedsMetadataRefresh(false);
      });
    }
  }, [getVideo, updateWithNewVideo, multiVideoMode, multiVideoList, isFetchingVideo, onUpdateMultiAdVideoList, copilotEnabled, needsMetadataRefresh, getNewVideoObject]);

  useEffect(() => {
    if (multiVideoMode && isVideoRequired && _.isEmpty(videoAd.videos)) {
      updateValidation([i18n.getString(_TL_('Please add a video.'))], { video: null }, false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { finalUrlForCopilot } = useVideoAdsCopilot();

  return (
    <>
      <UiBlocker
        blocking={isFetchingVideo}
        loadingText={i18n.getString(_TL_('Loading...'))}
        classes={{
          blockUiContainer: 'blocker-global',
          blockerIndicator: 'blocker-indicator',
        }}
      >
        {(multiVideoMode === false) ? (
          <Stack horizontal tokens={{ childrenGap: 10 }}>
            <div style={{ width: '170px' }}>
              <div>
                <VideoExperience
                  {...rest}
                  permissions={permissions}
                  videoList={video === null ? [] : [video]}
                  onVideoListUpdated={onUpdateVideoList}
                  onVideoUploadStatusUpdate={setIsUploading}
                  onErrorsUpdated={onErrorsUpdated}
                  key={`${idx}-videoExperienceValidated`}
                  i18n={i18n}
                  activityLogger={activity}
                  showErrors={false}
                  videoLabelHelpTopic={isCTV ? 'pop_BA_PremiumStreaming_Video' : 'pop_BA_VideoAds_VideoSource'}
                  CustomAddVideoControl={copilotEnabled ? AddVideoButton : undefined}
                  onRenderItemDropdown={copilotEnabled ? onRenderItemDropdownCoPilot : onRenderItemDropdown}
                  enableShowingErrors={false}
                />
              </div>
            </div>
            {!isGoogleImportVideoAdNeedsRepair && videoAd && videoAd.video && videoAd.video.quality !== undefined && (
              <PlacementInfoWithStars
                videoAd={videoAd}
                i18n={i18n}
              />)}
          </Stack>) : (
            <div>
              <VideoExperience
                {...rest}
                permissions={permissions}
                videoList={multiVideoList}
                onVideoListUpdated={onUpdateMultiAdVideoList}
                onVideoUploadStatusUpdate={setIsUploading}
                onErrorsUpdated={onErrorsUpdated}
                key={`${idx}-videoExperienceValidated`}
                i18n={i18n}
                multiVideoMode
                activityLogger={activity}
                showErrors={false}
                videoLabelHelpTopic="pop_BA_VideoAds_VideoSource"
                CustomAddVideoControl={AddVideoButton}
                onRenderItemDropdown={copilotEnabled ? onRenderItemDropDownForMultiModeWithCopilot : onRenderItemDropdownCoPilot}
                enableShowingErrors={false}
              />
            </div>)}
        {renderError}
        {copilotEnabled && (!multiVideoMode || (multiVideoMode && multiVideoList.length < 5)) &&
          <VideoAdsSuggestionCarousel
            i18n={i18n}
            onSelectVideoSuggestion={onChangeVideo}
            permissions={permissions}
            useBrandKit={useBrandKit}
            isBrandKitEmpty={isBrandKitEmpty}
            setUseBrandKit={setUseBrandKit}
            multiVideoMode={multiVideoMode}
          />
        }
      </UiBlocker>
      {
        copilotEnabled && video && video.template && (
          videoTemplateUIEnabled && !isLegacyTemplate ? (
            <VideoTemplateEditorContextWrapper
              i18n={i18n}
              isOpen={showVideoEditor}
              videoMetaData={video}
              onApply={onChangeVideo}
              setIsOpen={(newOpen) => {
                setShowVideoEditor(newOpen);
              }}
              isCTV={isCTV}
              cid={cid}
              aid={aid}
              finalUrl={finalUrlForCopilot}
              assetLibraryConfig={assetLibraryConfig}
              permissions={permissions}
              isAADUser={serverContext && serverContext.IsAADUser}
              serverContext={serverContext}
              perfMarker={perfMarker.createChild('VideoTemplateEditor')}
              isDialog
              imageAnimationEnabled={permissions && permissions.dynamic && permissions.dynamic.VideoAssetGenAdStudioGenAI}
              loadClipchampPlayer={loadClipchampPlayer}
            />
          ) : (
            <CopilotVideoEditor
              videoMetaData={video}
              isOpen={showVideoEditor}
              setIsOpen={(newOpen) => {
              setShowVideoEditor(newOpen);
            }}
              onApply={onChangeVideo}
              cid={cid}
              aid={aid}
              finalUrl={finalUrlForCopilot}
              assetLibraryConfig={assetLibraryConfig}
              permissions={permissions}
              isAADUser={serverContext && serverContext.IsAADUser}
              i18n={i18n}
              perfMarker={perfMarker.createChild('CopilotVideoEditor')}
              isCTV={isCTV}
            />
          )
        )
      }
      {
        copilotEnabled && multiVideoMode && (
          videoTemplateUIEnabled && showMultiVideoCopilotEditor && showMultiVideoCopilotEditor.video && showMultiVideoCopilotEditor.video.template && !legacyTemplates.includes(showMultiVideoCopilotEditor.video.template.templateId) ? (
            <VideoTemplateEditorContextWrapper
              i18n={i18n}
              isOpen={showMultiVideoCopilotEditor.isOpen}
              videoMetaData={showMultiVideoCopilotEditor.video}
              onApply={newVideo => onChangeMultiVideo(newVideo, showMultiVideoCopilotEditor.idx)}
              setIsOpen={(newOpen) => {
                setShowMultiVideoCopilotEditor({ isOpen: newOpen });
              }}
              isCTV={isCTV}
              cid={cid}
              aid={aid}
              finalUrl={finalUrlForCopilot}
              assetLibraryConfig={assetLibraryConfig}
              permissions={permissions}
              isAADUser={serverContext && serverContext.IsAADUser}
              serverContext={serverContext}
              perfMarker={perfMarker.createChild('VideoTemplateEditor')}
              isDialog
              imageAnimationEnabled={permissions && permissions.dynamic && permissions.dynamic.VideoAssetGenAdStudioGenAI}
              loadClipchampPlayer={loadClipchampPlayer}
            />
          ) : (
            <CopilotVideoEditor
              videoMetaData={showMultiVideoCopilotEditor.video}
              isOpen={showMultiVideoCopilotEditor.isOpen}
              setIsOpen={(newOpen) => {
              setShowMultiVideoCopilotEditor({ isOpen: newOpen });
            }}
              onApply={newVideo => onChangeMultiVideo(newVideo, showMultiVideoCopilotEditor.idx)}
              cid={cid}
              aid={aid}
              finalUrl={finalUrlForCopilot}
              assetLibraryConfig={assetLibraryConfig}
              permissions={permissions}
              isAADUser={serverContext && serverContext.IsAADUser}
              i18n={i18n}
              perfMarker={perfMarker.createChild('CopilotVideoEditor')}
              isCTV={isCTV}
            />
          )
        )
      }
    </>
  );
};


CustomComponent.propTypes = {
  onDataChange: PropTypes.func.isRequired,
  idx: PropTypes.number.isRequired,
  videoAd: PropTypes.shape({
    video: PropTypes.shape({
      Status: PropTypes.string,
      VideoTemplateUI: PropTypes.bool,
    }),
    messages: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  videoRequirementForDeal: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      VideoCopilotGeneration: PropTypes.bool,
    }),
  }).isRequired,
  renderError: PropTypes.node.isRequired,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func,
  }).isRequired,
  serverContext: PropTypes.shape({
    IsAADUser: PropTypes.bool,
  }).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  copilotEnabled: PropTypes.bool.isRequired,
  isVideoRequired: PropTypes.bool.isRequired,
};

const VideoExperienceWithValidation = _.compose(
  withControlErrorHandling,
  withUnvalidatedControlErrors,
  withError,
  withCustomValidation,
  withAssetLibraryServicesLoaded
)(CustomComponent);

const ValidatedVideoExperience = (props) => {
  const {
    videoAd, idx, i18n, multiVideoMode, isCTV, isVideoRequired, ...rest
  } = props;

  const {
    serverContext, preferenceService, cid, aid, oDataConfig,
  } = rest;

  const videoProp = andComp(entityIsAd, propertyIs(`Video.VideoMedia16X9_${idx}`));

  const validVideos = videoAd && Array.isArray(videoAd.videos) ? videoAd.videos.filter(video => video !== undefined) : [];

  const multiAdDataObj = { videos: validVideos };

  const dataObj = { video: videoAd ? videoAd.video : null };

  const processingMessage = i18n.getString(_TL_('We\'re processing your video and reviewing its quality, which may take some time to complete. Please provide your Final URL and select Save. Then, you can check your video\'s status from the ad grid.'));

  const passiveMessages = videoAd && videoAd.video && videoAd.video.Status === 'Draft' ? [processingMessage] : [];

  const updatedVideoAd = multiVideoMode ? { videos: validVideos } : videoAd;

  const activityLogger = useContext(ActivityContext);

  const assetLibraryConfig = {
    cid,
    aid,
    oDataConfig,
    dateFormat: _.get(serverContext, ['CurrentUser', 'CultureDateTimeFormat', 'ShortDatePattern']),
    timeZone: _.get(serverContext, ['CurrentAccount', 'TimeZone']),
    preferencesService: preferenceService,
    activityLogger,
  };

  return (
    <VideoExperienceWithValidation
      {...rest}
      assetLibraryConfig={assetLibraryConfig}
      videoAd={updatedVideoAd}
      multiVideoMode={multiVideoMode}
      idx={idx}
      i18n={i18n}
      validationProps={{
        field: multiVideoMode ? 'videos' : 'video',
        data: multiVideoMode ? multiAdDataObj : dataObj,
        initialValue: dataObj,
        passiveMessages: multiVideoMode ? [] : passiveMessages,
        customErrorRendering: true,
      }}
      formErrorProps={{ filter: videoProp }}
      isCTV={isCTV}
      isVideoRequired={isVideoRequired}
    />
  );
};

ValidatedVideoExperience.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  videoAd: PropTypes.shape({
    video: PropTypes.shape({
      Status: PropTypes.string,
    }),
  }).isRequired,
  idx: PropTypes.number.isRequired,
  multiVideoMode: PropTypes.bool,
  isCTV: PropTypes.bool,
  isVideoRequired: PropTypes.bool.isRequired,
  skipQualityValidation: PropTypes.bool,
};

ValidatedVideoExperience.defaultProps = {
  multiVideoMode: false,
  isCTV: false,
  skipQualityValidation: false,
};

const VideoExperienceWithPreference = withPreference(ValidatedVideoExperience);

const VideoExperienceWithPreferenceRecommendationFetch = (props) => {
  const copilotFetchTaskManager = useMemo(() => new TaskManager(), []);
  const { fetch, isBrandKitEmpty, fetchBrandKit } = useVideoAdsCopilot(true);
  const { useBrandKit } = props;

  useEffect(() => {
    fetchBrandKit();
  }, [fetchBrandKit]);

  useEffect(() => {
    if (props.copilotEnabled) {
      fetch(copilotFetchTaskManager, useBrandKit);
    }
  }, [props, copilotFetchTaskManager, fetch]);

  return <VideoExperienceWithPreference {...props} isBrandKitEmpty={isBrandKitEmpty} />;
};

export { VideoExperienceWithPreference as ValidatedVideoExperience };
export { VideoExperienceWithPreferenceRecommendationFetch as ValidatedVideoExperienceWithRecommendationFetch };
